import { useTranslation } from 'react-i18next'
import type { TeamStats } from '@/lib/stoccopong-stats'
import { cn } from '@/lib/utils'

type TeamStatsCardProps = { stats: TeamStats; teamName: string }

const LABEL = 'font-body text-[0.65rem] font-semibold uppercase tracking-wide text-ink-soft'

/**
 * Riepilogo della squadra selezionata come carta d'imbarco: nome e bilancio V-P nella striscia, poi bicchieri
 * segnati/subiti/differenza, media a partita, striscia di vittorie più lunga, pareggi vinti e airball.
 */
export function TeamStatsCard({ stats, teamName }: TeamStatsCardProps) {
  const { t } = useTranslation()

  const primaryStats = [
    { label: t('stoccoPong.stats.played'), value: stats.played },
    { label: t('stoccoPong.stats.cupsFor'), value: stats.cupsFor },
    { label: t('stoccoPong.stats.cupsAgainst'), value: stats.cupsAgainst },
    { label: t('stoccoPong.stats.diff'), value: stats.diff > 0 ? `+${stats.diff}` : stats.diff },
  ]

  return (
    <div data-testid="team-stats-card" className="max-w-3xl overflow-hidden rounded-xl border-2 border-ink bg-paper shadow-[4px_4px_0_var(--color-ink)]">
      <div className="flex items-center justify-between gap-3 bg-sea/15 px-5 pt-4 pb-3">
        <h3 className="min-w-0 truncate font-display text-3xl font-medium tracking-tight text-ink">{teamName}</h3>
        <span className="shrink-0 rounded-lg bg-ink px-2.5 py-1 font-body text-sm font-semibold tabular-nums text-paper">
          {t('stoccoPong.stats.record', { wins: stats.wins, losses: stats.losses })}
        </span>
      </div>
      <span aria-hidden="true" className="ticket-perforation block" />

      <div className="px-5 pt-4 pb-5">
        <dl className="grid grid-cols-4 gap-3 text-center">
          {primaryStats.map((item) => (
            <div key={item.label}>
              <dt className={LABEL}>{item.label}</dt>
              <dd className="font-display text-2xl tabular-nums text-ink">{item.value}</dd>
            </div>
          ))}
        </dl>

        <dl className="mt-4 grid grid-cols-4 gap-3 border-t border-line pt-4 text-center">
          <div>
            <dt className={LABEL}>{t('stoccoPong.stats.avgFor')}</dt>
            <dd className="font-display text-lg tabular-nums text-sea-ink">{stats.avgFor.toFixed(1)}</dd>
          </div>
          <div>
            <dt className={LABEL}>{t('stoccoPong.stats.bestStreak')}</dt>
            <dd className={cn('font-display text-lg tabular-nums', stats.bestStreak > 0 ? 'text-sea-ink' : 'text-ink-soft')}>
              {stats.bestStreak > 0 ? t('stoccoPong.stats.streakValue', { count: stats.bestStreak }) : '–'}
            </dd>
          </div>
          <div>
            <dt className={LABEL}>{t('stoccoPong.stats.redemptions')}</dt>
            <dd className={cn('font-display text-lg tabular-nums', stats.redemptions > 0 ? 'text-sun-ink' : 'text-ink-soft')}>
              {stats.redemptions > 0 ? stats.redemptions : '–'}
            </dd>
          </div>
          <div>
            <dt className={LABEL}>{t('stoccoPong.stats.airballs')}</dt>
            <dd className={cn('font-display text-lg tabular-nums', stats.airballs > 0 ? 'text-coral-ink' : 'text-ink-soft')}>
              {stats.airballs > 0 ? stats.airballs : '–'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
